import { useQuery } from '@tanstack/react-query';
import { resolveCustomerCompanies } from '@sakura-grp/database/services';
import { getBrowserClient } from '@/lib/supabase';
import { useHostessStore } from '@/stores/hostess';

/**
 * 顧客 1 件に対する「ログイン中ホステス視点」での代表名刺の会社名を解決する。
 *
 * 仕様: docs/domain/20260527-customer-company-context-display.md §営業履歴
 * - 閲覧者視点 (ログイン中の自分) で代表名刺の会社を表示
 * - activity の実行者が誰であっても同じ会社名 (顧客詳細画面の意図と一致)
 * - 自分の名刺集合に該当顧客の名刺がなければ undefined
 *
 * queryFn は undefined を返してはならない（TanStack Query v5 が console.error +
 * クエリ失敗扱いにする）ため内部では null で表現し、戻り値で undefined に変換する
 */
export function useResolvedCustomerCompany(customerId: string | undefined) {
  const { hostess } = useHostessStore();
  const hostessId = hostess?.id;

  const result = useQuery({
    queryKey: ['resolved-customer-company', hostessId ?? 'noviewer', customerId],
    queryFn: async () => {
      if (!customerId || !hostessId) return null;
      const client = getBrowserClient();
      const map = await resolveCustomerCompanies(client, [customerId], hostessId);
      return map.get(customerId)?.companyName ?? null;
    },
    enabled: !!customerId && !!hostessId,
    staleTime: 60 * 1000,
  });

  return result.data ?? undefined;
}
